import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, type UtilityTemplate } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/** How many `{{n}}` placeholders a template's body contains. */
function placeholdersOf(template: UtilityTemplate): number {
  const components = Array.isArray(template.components) ? template.components : [];
  const body = components.find(
    (part): part is { type?: string; text?: string } =>
      typeof part === 'object' && part !== null && (part as { type?: string }).type === 'BODY',
  );
  const text = body?.text ?? '';
  const found = new Set<string>();
  for (const match of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    if (match[1]) found.add(match[1]);
  }
  return found.size;
}

/** The body text, so the operator sees what they are about to send. */
function bodyOf(template: UtilityTemplate): string {
  const components = Array.isArray(template.components) ? template.components : [];
  const body = components.find(
    (part): part is { type?: string; text?: string } =>
      typeof part === 'object' && part !== null && (part as { type?: string }).type === 'BODY',
  );
  return body?.text ?? '';
}

/**
 * The body with each `{{n}}` replaced by what the operator typed for it, so the
 * preview is the sentence the customer will receive. A placeholder not yet
 * filled stays as `{{n}}`, which shows where the missing value goes.
 */
function filled(text: string, values: string[]): string {
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (placeholder, n: string) => {
    const value = values[Number(n) - 1]?.trim();
    return value ? value : placeholder;
  });
}

/**
 * What to do when Messenger refuses the reply because the window has closed.
 *
 * This appears only after that specific refusal, and nowhere else. A utility
 * template is not a better way to answer somebody who is waiting right now — it
 * is the only way to answer somebody whose 24 hours ran out, and offering it
 * before then would invite its use as an ordinary reply, which is exactly the
 * misuse Meta withdraws the permission for.
 *
 * Only approved templates are offered. A pending or rejected one would fail at
 * send with a message about template status, after the operator had already
 * filled in the fields.
 */
export function UtilityFallback({
  itemId,
  pageId,
  onSent,
}: {
  itemId: string;
  pageId: string;
  onSent: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [values, setValues] = useState<string[]>([]);

  const templates = useQuery({
    queryKey: ['message-templates', pageId],
    queryFn: () => api.messageTemplates(pageId),
  });

  const approved = (templates.data?.templates ?? []).filter(
    (template) => template.status.toUpperCase() === 'APPROVED',
  );
  const chosen = approved.find((template) => template.name === name);
  const needed = chosen ? placeholdersOf(chosen) : 0;

  const send = useMutation({
    mutationFn: () =>
      api.sendUtilityMessage(itemId, {
        templateName: name,
        language: chosen?.language ?? 'ar',
        bodyParams: values.slice(0, needed),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      onSent();
    },
  });

  const ready = Boolean(chosen) && values.slice(0, needed).every((value) => value?.trim());

  return (
    <div className="utility-fallback">
      <p className="muted small">
        <Icon.clock /> {t('utility.windowClosed')}
      </p>

      {templates.isError && <ErrorNotice error={templates.error} />}

      {templates.data && approved.length === 0 ? (
        // Not an error: it is the ordinary state before any template exists, and
        // saying where to make one is more use than saying there are none.
        <p className="muted small">{t('utility.noneApproved')}</p>
      ) : (
        <div className="utility-form">
          <select
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setValues([]);
            }}
          >
            <option value="">{t('utility.chooseTemplate')}</option>
            {approved.map((template) => (
              <option key={template.id} value={template.name}>
                {template.name}
              </option>
            ))}
          </select>

          {chosen && (
            <>
              <p className="utility-preview">{filled(bodyOf(chosen), values)}</p>
              {Array.from({ length: needed }, (_, index) => (
                <input
                  key={index}
                  value={values[index] ?? ''}
                  placeholder={`{{${index + 1}}}`}
                  onChange={(event) => {
                    const next = [...values];
                    next[index] = event.target.value;
                    setValues(next);
                  }}
                />
              ))}
              <button
                type="button"
                className="primary"
                disabled={!ready || send.isPending}
                onClick={() => send.mutate()}
              >
                <Icon.send />
                {send.isPending ? t('inbox.sending') : t('utility.send')}
              </button>
            </>
          )}
        </div>
      )}

      {send.isError && <ErrorNotice error={send.error} />}
    </div>
  );
}
